import React, { useState } from 'react';
import {
  Modal, Box, Button, Alert, SpaceBetween, FormField, Select, Textarea
} from '@cloudscape-design/components';
import { imageStudioApi } from './imageStudioApi.js';

const TOOL_OPTIONS = [
  { label: 'Overall / All tools', value: 'overall' },
  { label: '🖼️ Resize', value: 'resize' },
  { label: '✂️ Background', value: 'background' },
  { label: '📸 Lifestyle', value: 'lifestyle' },
  { label: '🔄 Convert', value: 'convert' },
];

const CATEGORY_OPTIONS = [
  { label: 'Image quality', value: 'image_quality' },
  { label: "Scene didn't match expectation", value: 'scene_mismatch' },
  { label: 'Product looks wrong / distorted', value: 'product_issue' },
  { label: 'Too slow', value: 'performance' },
  { label: 'UI / workflow confusing', value: 'ux' },
  { label: 'Other', value: 'other' },
];

// Comment becomes required when the rating is <= this value.
const LOW_RATING_THRESHOLD = 2;

export default function FeedbackModal({ visible, onDismiss, username }) {
  const [rating, setRating] = useState(0);               // 0 means not yet picked
  const [hoverRating, setHoverRating] = useState(0);
  const [tool, setTool] = useState(TOOL_OPTIONS[0]);
  const [category, setCategory] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setRating(0);
    setHoverRating(0);
    setTool(TOOL_OPTIONS[0]);
    setCategory(null);
    setComment('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const commentRequired = rating > 0 && rating <= LOW_RATING_THRESHOLD;
  const canSubmit = rating > 0
    && (!commentRequired || comment.trim().length > 0)
    && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await imageStudioApi.submitFeedback({
        rating,
        tool: tool?.value || 'overall',
        category: category?.value || '',
        comment: comment.trim(),
        user: username || 'unknown',
        page: typeof window !== 'undefined' ? window.location?.pathname : '',
      });
      setSubmitted(true);
      // Auto-close after a short delay so the user sees the success state.
      setTimeout(() => handleDismiss(), 1200);
    } catch (err) {
      setError(err.message || 'Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close feedback"
      header="💬 Share your feedback"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={submitting}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {submitted && <Alert type="success">Thanks — feedback received.</Alert>}
        {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

        <FormField
          label="How would you rate your experience?"
          description="Required."
        >
          <StarRating
            value={rating}
            hoverValue={hoverRating}
            onChange={setRating}
            onHover={setHoverRating}
            disabled={submitting}
          />
        </FormField>

        <FormField label="Which tool is this about?">
          <Select
            selectedOption={tool}
            onChange={({ detail }) => setTool(detail.selectedOption)}
            options={TOOL_OPTIONS}
            disabled={submitting}
          />
        </FormField>

        <FormField label="What was it about? (optional)">
          <Select
            selectedOption={category}
            onChange={({ detail }) => setCategory(detail.selectedOption)}
            options={CATEGORY_OPTIONS}
            placeholder="Pick a category"
            disabled={submitting}
          />
        </FormField>

        <FormField
          label="Comments"
          description={
            commentRequired
              ? 'Required for ratings of 2 or lower — tell us what went wrong.'
              : 'Anything specific you want us to know? (optional)'
          }
          errorText={commentRequired && comment.trim().length === 0 ? 'Please add a short comment.' : undefined}
        >
          <Textarea
            value={comment}
            onChange={({ detail }) => setComment(detail.value)}
            placeholder={
              commentRequired
                ? "e.g., The product looked stretched in all 3 variations"
                : "e.g., Liked the scene, wanted more variations"
            }
            rows={3}
            disabled={submitting}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

/** Simple 5-star rating component. No extra dependency. */
function StarRating({ value, hoverValue, onChange, onHover, disabled }) {
  const stars = [1, 2, 3, 4, 5];
  const effective = hoverValue || value;

  return (
    <div
      role="radiogroup"
      aria-label="Rating"
      style={{ display: 'inline-flex', gap: 4 }}
      onMouseLeave={() => !disabled && onHover(0)}
    >
      {stars.map((n) => {
        const filled = n <= effective;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onClick={() => !disabled && onChange(n)}
            onMouseEnter={() => !disabled && onHover(n)}
            onFocus={() => !disabled && onHover(n)}
            onBlur={() => !disabled && onHover(0)}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled ? 'default' : 'pointer',
              fontSize: 28,
              lineHeight: 1,
              padding: '0 2px',
              color: filled ? '#f2c94c' : '#d1d5db',
              transition: 'color 0.12s',
            }}
          >
            {filled ? '★' : '☆'}
          </button>
        );
      })}
      {effective > 0 && (
        <Box
          fontSize="body-s"
          color="text-body-secondary"
          margin={{ left: 's' }}
        >
          {['', 'Poor', 'Fair', 'OK', 'Good', 'Great'][effective]}
        </Box>
      )}
    </div>
  );
}
